<template>
  <TextBlob headingSize="m">
    <template #heading> Login </template>
    <template #text>
      <div>
        <div class="flex w-full">
          <div class="flex w-full justify-center">
            <div class="w-[90%] sm:w-3/4 max-w-[500px]">
              <el-input
                v-model="email"
                ref="bizReviewLink"
                placeholder=""
                style="width: 100%"
                @keydown.enter.prevent="submitLogin"
              >
                <template #prepend>
                  <span class="font-bold w-[110px] text-right">Email</span>
                </template>
              </el-input>
            </div>
          </div>
        </div>
        <div class="flex mt-4 flex-col w-full">
          <div class="flex w-full justify-center">
            <div class="w-[90%] sm:w-3/4 max-w-[500px]">
              <el-input
                v-model="password"
                type="password"
                placeholder=""
                @keydown.enter.prevent="submitLogin"
              >
                <template #prepend>
                  <span class="font-bold w-[110px] text-right">Password</span>
                </template>
              </el-input>
            </div>
          </div>
          <div
            class="text-center underline cursor-pointer w-fit mx-auto mt-2"
            @click="userStore.activeRoute = 'forgot'"
            :data-track="
              JSON.stringify({
                id: 'login-forgot-password',
                type: 'login-link',
                action: 'forgot-password',
              })
            "
          >
            Forgot your password?
          </div>
          <div class="flex flex-col w-full">
            <div
              v-for="button in buttons"
              :key="button.text"
              class="mx-auto w-full md:w-2/3 flex justify-center"
              :id="button.text"
            >
              <el-button
                class="mt-4 w-1/2"
                :type="button.type"
                size="large"
                plain
                @click="button.click"
                :data-track="
                  JSON.stringify({
                    id: `login-${button.text
                      .toLowerCase()
                      .replace(/\s+/g, '-')}`,
                    type: 'login-button',
                    action: button.text,
                  })
                "
              >
                <span class="mr-2">
                  <GoogleLogo v-if="button.text == 'Login with Google'" />
                </span>
                <div v-if="loadingButton === button.text">
                  <PulseLoader
                    :class="{ 'inverse-color': button.text === 'Login' }"
                  />
                </div>
                <div v-else>
                  {{ button.text }}
                </div>
              </el-button>
            </div>
          </div>
        </div>
      </div>
      <LoginError :error="errorMessage" />
    </template>
  </TextBlob>
</template>

<script setup>
import { ref } from "vue";
import TextBlob from "@/components/TextBlob.vue";
import LoginError from "./LoginError.vue";
import router from "@/router";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { useUser } from "@/stores";
const userStore = useUser();
import api from "@/composables/api";
import PulseLoader from "vue-spinner/src/PulseLoader.vue";
import GoogleLogo from "@/components/GoogleLogo.vue";

import { useError } from "@/composables/useError";
const { errorMessage, processError } = useError();
const email = ref("");
const password = ref("");
const loadingButton = ref("");

const registerRoute = () => {
  userStore.activeRoute = "register";
};

const submitLogin = async () => {
  try {
    loadingButton.value = "Login";
    //delay for testing
    // await new Promise(resolve => setTimeout(resolve, 2000));
    const auth = getAuth();
    const creds = await signInWithEmailAndPassword(
      auth,
      email.value,
      password.value
    );
    // Clear any existing session ID to ensure new analytics session
    window.sessionStorage.removeItem("analytics_session_id");
    userStore.user.value = creds.user;
    await userStore.getSettings();
    router.push("/explorer/dashboard");
  } catch (error) {
    // console.log("error:::", error)
    loadingButton.value = "";
    processError(error);
  }
};

const submitLoginGoogle = async () => {
  try {
    loadingButton.value = "Login with Google";
    const provider = new GoogleAuthProvider();
    const auth = getAuth();
    const userCredential = await signInWithPopup(auth, provider);
    const user = userCredential.user;
    // Clear any existing session ID to ensure new analytics session
    window.sessionStorage.removeItem("analytics_session_id");
    userStore.user.value = user;
    try {
      await api("v1/user/check", {
        method: "POST",
        body: {
          email: user.email,
        },
      });
    } catch (error) {
      loadingButton.value = "";
      if (error.status === 404) {
        await createUser(user);
      } else {
        processError(error);
        console.log("error", error);
      }
    }

    router.push("/explorer/dashboard");
  } catch (error) {
    loadingButton.value = "";
    processError(error);
    console.log("error", error);
  }
};

const createUser = async (user) => {
  try {
    await api("v1/user/create", {
      method: "POST",
      body: {
        email: user.email,
        displayName: user.displayName,
      },
    });
  } catch (error) {
    processError(error);
  }
};

const buttons = ref([
  {
    text: "Login",
    type: "success",
    click: submitLogin,
  },
  {
    text: "Login with Google",
    type: "info",
    click: submitLoginGoogle,
  },
  {
    text: "Register",
    type: "primary",
    click: () => {
      userStore.activeRoute = "register";
    },
  },
  {
    text: "Back to Home",
    type: "danger",
    click: () => {
      userStore.activeTab = "";
      router.push("/");
    },
  },
]);

// TODO: reroute on already logged in
// onMounted(() => {
//   const auth = getAuth();

//   const unsubscribe = auth.onAuthStateChanged((user) => {
//     if (user) {
//       // console.log("User is already signed in, redirect!")
//       store.user = user;
//       if (store.activeTab && store.activeTab != "explorer" && store.activeTab != "login") {
//         router.push("/explorer/" + store.activeTab)
//       } else {
//         router.push("/explorer")
//       }
//     } else {
//       // console.log("No user is signed in.")
//       store.user = null;
//     }
//   });

//   // Unsubscribe from the listener when the component is unmounted
//   onBeforeUnmount(unsubscribe);
// });
</script>

<style>
.inverse-color {
  filter: invert(1);
}
</style>
